import { captureReferralCode, ReferralCaptureError } from '@query/referral'
import { useIsAuthenticated } from '@root/context/AuthContext'
import { useLocalStorage } from '@root/hooks'
import { parseReferralCode } from './parseReferral'
import { useEffect, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'

export const REFERRAL_LOCAL_STORAGE_KEY = 'referral_code'

export const useStoreReferral = () => {
  const [t] = useTranslation()
  const isAuthenticated = useIsAuthenticated()
  const parsed = useMemo(() => {
    if (typeof window === 'undefined') return undefined
    return parseReferralCode(window.location.href)
  }, [])
  const [storedCode, setStoredCode] = useLocalStorage<string | undefined>(
    REFERRAL_LOCAL_STORAGE_KEY,
    parsed
  )
  const capturedCodeRef = useRef<string | undefined>(undefined)
  const invalidCodeRef = useRef<string | undefined>(undefined)
  const codeToCapture = parsed ?? storedCode

  useEffect(() => {
    if (!parsed || parsed === storedCode) return
    setStoredCode(parsed)
  }, [parsed, setStoredCode, storedCode])

  useEffect(() => {
    if (isAuthenticated !== false || !codeToCapture) return
    if (invalidCodeRef.current === codeToCapture) return
    if (capturedCodeRef.current === codeToCapture) return

    capturedCodeRef.current = codeToCapture
    void captureReferralCode(undefined, { code: codeToCapture }).catch(error => {
      if (error instanceof ReferralCaptureError && error.code === 'invalid_code') {
        invalidCodeRef.current = codeToCapture
        setStoredCode(undefined)
        toast.error(t('settings.referral.captureInvalidCode'))
        return
      }

      capturedCodeRef.current = undefined
    })
  }, [codeToCapture, isAuthenticated, setStoredCode, t])
}
